import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, CardContent } from '@mui/material';
import { AccountBalance as FinanceIcon } from '@mui/icons-material';
import { PageContainer, PageCard } from '../../common/components/styled-ui-elements';
import { PageLoading, StatusPage, CenterSelector } from '../../common/components';
import { useAppSelector } from '../../../app/hooks';
import { useGetMyProfileQuery } from '../../../services/ProfileService';
import { useCenterSelection } from '../../common/hooks/useCenterSelection';
import { RefundDashboardHeader, ErrorState, RefundDashboardGrid, useRefundDashboardData } from '../components/dashboard';



// Main Dashboard Component
const RefundDashboard: React.FC = () => {
  const [searchParams] = useSearchParams();
  const company = searchParams.get('company') || '';
  const center = searchParams.get('center') || '';
  
  // Get profile data as fallback
  const selectedCenter = useAppSelector(state => state.center.selectedCenter);
  const { data: profile } = useGetMyProfileQuery();
  const fallbackCompany = profile?.organization || '';
  const { handleCenterChange } = useCenterSelection();
  
  // Use URL params or fallback to profile data
  const actualCompany = company || fallbackCompany;
  const actualCenter = center || selectedCenter || '';

  const { summaries, isLoading, hasError, totalCount, totalAmount } = useRefundDashboardData(actualCompany, actualCenter);

  // Show configuration required if missing company
  if (!actualCompany) {
    return (
      <PageContainer>
        <StatusPage
          variant="configuration-required"
          icon={FinanceIcon}
          title="Configuration Required"
          message="To access the refund dashboard, please ensure company is properly configured in your session."
          chips={[{ label: `Company: ${actualCompany || 'Not configured'}`, color: actualCompany ? 'success' : 'warning' }]}
          footnote="Contact your system administrator if you need assistance with configuration"
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Box display="flex" justifyContent="center">
        <PageCard sx={{ 
          background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
          minWidth: '1000px',
          maxWidth: '1400px',
          width: '100%'
        }}>
          <RefundDashboardHeader 
            company={actualCompany}
            center={actualCenter}
            totalCount={totalCount}
            totalAmount={totalAmount}
          />
          
          <CardContent sx={{ 
            p: 4, 
            backgroundColor: 'transparent'
          }}>
            <Box sx={{ mb: 3 }}>
              <CenterSelector value={actualCenter} onChange={handleCenterChange} />
            </Box>
            {!actualCenter ? (
              <StatusPage
                variant="configuration-required"
                icon={FinanceIcon}
                title="Select a Center"
                message="Please select a center from the dropdown above to view the refund dashboard."
              />
            ) : isLoading ? (
              <PageLoading message="Loading Dashboard Data" subMessage="Retrieving refund information..." />
            ) : hasError ? (
              <ErrorState />
            ) : (
              <RefundDashboardGrid 
                summaries={summaries}
                company={actualCompany}
                center={actualCenter}
              />
            )}
          </CardContent>
        </PageCard>
      </Box>
    </PageContainer>
  );
};

export default RefundDashboard;